const DB_NAME = "elsa_offline";
const DB_VERSION = 1;
const STORE_QUEUE = "request_queue";
const STORE_CACHE = "cache";
const STORE_ID_MAP = "id_map";

const LS_PREFIX = "elsa_offline_";
const LS_QUEUE_KEY = `${LS_PREFIX}queue`;
const LS_CACHE_KEY = `${LS_PREFIX}cache`;
const LS_MAP_KEY = `${LS_PREFIX}map`;
const LS_ID_KEY = `${LS_PREFIX}id`;

const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours
const ID_MAP_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 jours
const PURGE_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 heures

const hasIndexedDB = typeof indexedDB !== "undefined";
let lastPurgeAt = 0;

function readLocal(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (error) {
    return fallback;
  }
}

function writeLocal(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore storage errors (quota, privacy mode, etc.)
  }
}

function nextLocalId() {
  const current = readLocal(LS_ID_KEY, 0);
  const next = Number(current || 0) + 1;
  writeLocal(LS_ID_KEY, next);
  return next;
}

function isEntryExpired(entry, ttlMs, now = Date.now()) {
  if (!entry || typeof entry !== "object") {
    return false;
  }

  const expiresAt = Number(entry.expiresAt);
  if (Number.isFinite(expiresAt) && expiresAt > 0) {
    return now >= expiresAt;
  }

  const updatedAt = Number(entry.updatedAt);
  if (Number.isFinite(updatedAt) && updatedAt > 0) {
    return now - updatedAt > ttlMs;
  }

  return false;
}

function purgeLocalCache(now = Date.now()) {
  const cache = readLocal(LS_CACHE_KEY, {});
  if (!cache || typeof cache !== "object") return false;

  let changed = false;
  Object.entries(cache).forEach(([key, value]) => {
    if (isEntryExpired(value, CACHE_TTL_MS, now)) {
      delete cache[key];
      changed = true;
    }
  });

  if (changed) {
    writeLocal(LS_CACHE_KEY, cache);
  }
  return changed;
}

function purgeLocalIdMap(now = Date.now()) {
  const map = readLocal(LS_MAP_KEY, {});
  if (!map || typeof map !== "object") return false;

  let changed = false;
  Object.entries(map).forEach(([tempId, value]) => {
    if (isEntryExpired(value, ID_MAP_TTL_MS, now)) {
      delete map[tempId];
      changed = true;
    }
  });

  if (changed) {
    writeLocal(LS_MAP_KEY, map);
  }
  return changed;
}

async function purgeIndexedStoreEntries(storeName, ttlMs, getKey) {
  return withStore(storeName, "readwrite", async (store) => {
    const rows = (await wrapRequest(store.getAll())) || [];
    const now = Date.now();

    for (const row of rows) {
      if (!isEntryExpired(row, ttlMs, now)) continue;
      const key = getKey(row);
      if (typeof key === "undefined" || key === null) continue;
      await wrapRequest(store.delete(key));
    }
    return true;
  });
}

export async function purgeExpiredOfflineData(force = false) {
  const now = Date.now();
  if (!force && now - lastPurgeAt < PURGE_INTERVAL_MS) {
    return false;
  }

  lastPurgeAt = now;

  if (!hasIndexedDB) {
    const cachePurged = purgeLocalCache(now);
    const mapPurged = purgeLocalIdMap(now);
    return cachePurged || mapPurged;
  }

  try {
    await purgeIndexedStoreEntries(STORE_CACHE, CACHE_TTL_MS, (row) => row.key);
    await purgeIndexedStoreEntries(
      STORE_ID_MAP,
      ID_MAP_TTL_MS,
      (row) => row.tempId
    );
    return true;
  } catch (error) {
    // Fallback best effort en localStorage si IndexedDB échoue
    const cachePurged = purgeLocalCache(now);
    const mapPurged = purgeLocalIdMap(now);
    return cachePurged || mapPurged;
  }
}

function openDb() {
  if (!hasIndexedDB) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        db.createObjectStore(STORE_QUEUE, { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(STORE_ID_MAP)) {
        db.createObjectStore(STORE_ID_MAP, { keyPath: "tempId" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function wrapRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore(storeName, mode, operation) {
  const db = await openDb();
  if (!db) return operation(null, true);

  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);

    Promise.resolve(operation(store, false))
      .then((result) => {
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
      })
      .catch(reject);
  });
}

export async function cacheGet(key) {
  void purgeExpiredOfflineData();

  if (!hasIndexedDB) {
    const cache = readLocal(LS_CACHE_KEY, {});
    const entry = cache[key] || null;
    if (entry && isEntryExpired(entry, CACHE_TTL_MS)) {
      delete cache[key];
      writeLocal(LS_CACHE_KEY, cache);
      return null;
    }
    return entry;
  }

  const result = await withStore(STORE_CACHE, "readonly", async (store) => {
    const result = await wrapRequest(store.get(key));
    return result || null;
  });

  if (result && isEntryExpired(result, CACHE_TTL_MS)) {
    await withStore(STORE_CACHE, "readwrite", async (writeStore) => {
      await wrapRequest(writeStore.delete(key));
      return true;
    });
    return null;
  }

  return result;
}

export async function cacheSet(key, value) {
  void purgeExpiredOfflineData();
  const now = Date.now();
  const payload = {
    key,
    value,
    updatedAt: now,
    expiresAt: now + CACHE_TTL_MS,
  };

  if (!hasIndexedDB) {
    const cache = readLocal(LS_CACHE_KEY, {});
    cache[key] = payload;
    writeLocal(LS_CACHE_KEY, cache);
    return payload;
  }

  return withStore(STORE_CACHE, "readwrite", async (store) => {
    await wrapRequest(store.put(payload));
    return payload;
  });
}

export async function queueAdd(entry) {
  const payload = {
    ...entry,
    status: entry.status || "pending",
    createdAt: entry.createdAt || Date.now(),
  };

  if (!hasIndexedDB) {
    const queue = readLocal(LS_QUEUE_KEY, []);
    const id = nextLocalId();
    queue.push({ ...payload, id });
    writeLocal(LS_QUEUE_KEY, queue);
    return id;
  }

  return withStore(STORE_QUEUE, "readwrite", async (store) => {
    const id = await wrapRequest(store.add(payload));
    return id;
  });
}

export async function queueGetAll() {
  if (!hasIndexedDB) {
    const queue = readLocal(LS_QUEUE_KEY, []);
    return Array.isArray(queue) ? queue : [];
  }

  return withStore(STORE_QUEUE, "readonly", async (store) => {
    const result = await wrapRequest(store.getAll());
    return result || [];
  });
}

export async function queueUpdate(entry) {
  if (!entry || typeof entry.id === "undefined") return null;

  if (!hasIndexedDB) {
    const queue = readLocal(LS_QUEUE_KEY, []);
    const index = queue.findIndex((item) => item.id === entry.id);
    if (index !== -1) {
      queue[index] = { ...queue[index], ...entry };
      writeLocal(LS_QUEUE_KEY, queue);
      return queue[index];
    }
    return null;
  }

  return withStore(STORE_QUEUE, "readwrite", async (store) => {
    await wrapRequest(store.put(entry));
    return entry;
  });
}

export async function queueDelete(id) {
  if (!hasIndexedDB) {
    const queue = readLocal(LS_QUEUE_KEY, []);
    const nextQueue = queue.filter((item) => item.id !== id);
    writeLocal(LS_QUEUE_KEY, nextQueue);
    return true;
  }

  return withStore(STORE_QUEUE, "readwrite", async (store) => {
    await wrapRequest(store.delete(id));
    return true;
  });
}

export async function idMapGet(tempId) {
  if (!tempId) return null;
  void purgeExpiredOfflineData();

  if (!hasIndexedDB) {
    const map = readLocal(LS_MAP_KEY, {});
    const entry = map[tempId];
    if (!entry) return null;

    if (typeof entry === "object" && entry !== null) {
      if (isEntryExpired(entry, ID_MAP_TTL_MS)) {
        delete map[tempId];
        writeLocal(LS_MAP_KEY, map);
        return null;
      }
      return entry.serverId || null;
    }

    // Compatibilité anciens enregistrements (valeur primitive)
    return entry;
  }

  const result = await withStore(STORE_ID_MAP, "readonly", async (store) => {
    const result = await wrapRequest(store.get(tempId));
    return result || null;
  });

  if (!result) return null;

  if (isEntryExpired(result, ID_MAP_TTL_MS)) {
    await withStore(STORE_ID_MAP, "readwrite", async (writeStore) => {
      await wrapRequest(writeStore.delete(tempId));
      return true;
    });
    return null;
  }

  return result.serverId;
}

export async function idMapSet(tempId, serverId) {
  if (!tempId || !serverId) return null;
  void purgeExpiredOfflineData();
  const now = Date.now();

  if (!hasIndexedDB) {
    const map = readLocal(LS_MAP_KEY, {});
    map[tempId] = {
      tempId,
      serverId,
      updatedAt: now,
      expiresAt: now + ID_MAP_TTL_MS,
    };
    writeLocal(LS_MAP_KEY, map);
    return serverId;
  }

  return withStore(STORE_ID_MAP, "readwrite", async (store) => {
    await wrapRequest(
      store.put({
        tempId,
        serverId,
        updatedAt: now,
        expiresAt: now + ID_MAP_TTL_MS,
      })
    );
    return serverId;
  });
}

export function isIndexedDbAvailable() {
  return hasIndexedDB;
}
